/* global navigator, window */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, KeyboardAvoidingView, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { launchImageLibrary } from 'react-native-image-picker';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { createChatClient } from '../../runtime/mobile/chatClient';
import { pickFile } from '../../runtime/mobile/filePicker';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { presenceStatus } from '../../runtime/presence';
import { colors, radius, spacing, typography } from '../../config/theme';

const toMs = (v) => (v ? new Date(v).getTime() || 0 : 0);
const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase() || '?';
const nameColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `hsl(${h}, 70%, 62%)`;
};
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const dayKey = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
const dayLabel = (ms) => {
  const d = new Date(ms); const now = new Date();
  const dk = dayKey(ms);
  if (dk === dayKey(now.getTime())) return 'Сегодня';
  if (dk === dayKey(now.getTime() - 864e5)) return 'Вчера';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`}`;
};
const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`;
  return `${(b / 1048576).toFixed(1)} МБ`;
};
const copyText = (t) => {
  try { if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(t); } catch { /* ignore */ }
};
const openUrl = (url) => {
  try { if (typeof window !== 'undefined' && window.open) window.open(url, '_blank'); } catch { /* ignore */ }
};

export default function ChatThreadScreen({ route }) {
  const { channelId, title } = route.params || {};
  const { user } = useAuth();
  const { markRead, channels } = useChat();
  const client = useMemo(() => createChatClient(user), [user]);
  const channel = useMemo(() => channels.find((c) => c.id === channelId), [channels, channelId]);
  const peerSeen = channel?.kind === 'direct' ? presenceStatus(channel.otherLastSeenAt) : null;
  const subtitle = channel?.kind === 'group' && channel.memberIds
    ? `${channel.memberIds.length} участник${channel.memberIds.length === 1 ? '' : channel.memberIds.length < 5 ? 'а' : 'ов'}`
    : (peerSeen ? (peerSeen.online ? 'в сети' : `был(а) в сети ${peerSeen.text}`) : null);

  const [messages, setMessages] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [text, setText] = useState('');
  const [pending, setPending] = useState(null); // { dataUrl, kind:'image'|'file', name?, size? }
  const [viewer, setViewer] = useState(null);
  const [menu, setMenu] = useState(false); // attach menu
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);

  const merge = useCallback((incoming) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const next = [...prev, ...incoming.filter((m) => !seen.has(m.id))];
      next.sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
      lastIdRef.current = next[next.length - 1]?.id || null;
      return next;
    });
  }, []);

  useEffect(() => { if (messages.length) markRead(channelId, messages[messages.length - 1]); }, [messages, channelId, markRead]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const rows = await client.listMessages(channelId, lastIdRef.current); if (alive) merge(rows); } catch { /* keep */ }
    };
    tick();
    const id = setInterval(tick, 2500);
    return () => { alive = false; clearInterval(id); };
  }, [client, channelId, merge]);

  useEffect(() => { const t = setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 80); return () => clearTimeout(t); }, [messages.length, outbox.length]);

  const attachPhoto = useCallback(async () => {
    setMenu(false);
    try {
      const res = await launchImageLibrary({ mediaType: 'photo', selectionLimit: 1, includeBase64: true, maxWidth: 1200, maxHeight: 1200, quality: 0.55 });
      const a = res?.assets?.[0];
      if (!a) return;
      const dataUrl = a.uri && a.uri.startsWith('data:') ? a.uri : (a.base64 ? `data:${a.type || 'image/jpeg'};base64,${a.base64}` : null);
      if (dataUrl) setPending({ dataUrl, kind: 'image' });
    } catch { setError('Не удалось открыть фото'); }
  }, []);

  const attachFile = useCallback(async () => {
    setMenu(false);
    try {
      const f = await pickFile();
      if (!f) return;
      if ((f.size || 0) > 10 * 1024 * 1024) { setError('Файл больше 10 МБ'); return; }
      const isImg = /^image\//.test(f.type || '');
      setPending({ dataUrl: f.dataUrl, kind: isImg ? 'image' : 'file', name: f.name, size: f.size });
    } catch { setError('Не удалось выбрать файл'); }
  }, []);

  const deliver = useCallback(async (item) => {
    setOutbox((prev) => prev.map((o) => (o.tempId === item.tempId ? { ...o, status: 'sending' } : o)));
    try {
      const created = await client.send({
        channelId,
        text: item.text || null,
        mediaType: item.media ? item.media.kind : null,
        mediaUrl: item.media ? item.media.dataUrl : null,
        mediaName: item.media ? item.media.name || null : null,
      });
      if (created?.id) merge([created]);
      setOutbox((prev) => prev.filter((o) => o.tempId !== item.tempId));
    } catch (e) {
      setError(readableMobileError(e));
      setOutbox((prev) => prev.map((o) => (o.tempId === item.tempId ? { ...o, status: 'failed' } : o)));
    }
  }, [client, channelId, merge]);

  const send = () => {
    const body = text.trim();
    if (!body && !pending) return;
    setError(null);
    const item = { tempId: `t${Date.now()}${Math.random().toString(36).slice(2, 6)}`, text: body || null, media: pending || null, status: 'sending', createdAt: new Date().toISOString() };
    setOutbox((prev) => [...prev, item]);
    setText('');
    setPending(null);
    deliver(item);
  };

  const renderMedia = (m, mine) => {
    if (!m.mediaUrl) return null;
    if (m.mediaType === 'file') {
      return (
        <Pressable style={styles.file} onPress={() => openUrl(m.mediaUrl)}>
          <Text style={styles.fileIcon}>📄</Text>
          <View style={styles.grow}>
            <Text style={[styles.fileName, mine && styles.msgTextMine]} numberOfLines={1}>{m.mediaName || 'файл'}</Text>
            <Text style={[styles.fileMeta, mine && styles.msgTimeMine]}>нажми, чтобы скачать</Text>
          </View>
        </Pressable>
      );
    }
    return (
      <Pressable onPress={() => setViewer(m.mediaUrl)}>
        <Image source={{ uri: m.mediaUrl }} style={styles.image} resizeMode="cover" />
      </Pressable>
    );
  };

  let lastDay = null;

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.root}>
      {subtitle ? (
        <Text style={styles.subhead}>
          {title} · <Text style={peerSeen?.online ? styles.subheadOnline : null}>{subtitle}</Text>
        </Text>
      ) : null}
      <ScrollView ref={scrollRef} contentContainerStyle={styles.list}>
        {messages.map((m, i) => {
          const rows = [];
          const dk = dayKey(toMs(m.createdAt));
          if (dk !== lastDay) { lastDay = dk; rows.push(<Text key={`d${m.id}`} style={styles.daySep}>{dayLabel(toMs(m.createdAt))}</Text>); }
          if (m.kind === 'system') { rows.push(<Text key={m.id} style={styles.system}>{m.text}</Text>); return rows; }
          const mine = m.authorId === user?.uid;
          const prev = messages[i - 1];
          const firstOfRun = !prev || prev.kind === 'system' || prev.authorId !== m.authorId || dayKey(toMs(prev.createdAt)) !== dk;
          const name = m.authorName || 'Без имени';
          rows.push(
            <Pressable
              key={m.id}
              onLongPress={() => m.text && copyText(m.text)}
              delayLongPress={350}
              style={[styles.msgRow, mine ? styles.right : styles.left, { marginTop: firstOfRun ? spacing.sm : 2 }]}
            >
              {!mine && (firstOfRun
                ? <View style={[styles.avatar, { backgroundColor: nameColor(name) }]}><Text style={styles.avatarText}>{initials(name)}</Text></View>
                : <View style={styles.avatarSpacer} />)}
              <View style={styles.bubbleWrap}>
                {!mine && firstOfRun && <Text style={[styles.author, { color: nameColor(name) }]}>{name}</Text>}
                <View style={[
                  styles.bubble,
                  mine ? styles.bubbleMine : styles.bubbleOther,
                  mine ? (firstOfRun ? styles.tailMineFirst : styles.tailMine) : (firstOfRun ? styles.tailOtherFirst : styles.tailOther),
                ]}>
                  {renderMedia(m, mine)}
                  {m.text ? <Text style={[styles.msgText, mine && styles.msgTextMine]}>{m.text}</Text> : null}
                  <Text style={[styles.msgTime, mine && styles.msgTimeMine]}>{hhmm(toMs(m.createdAt))}</Text>
                </View>
              </View>
            </Pressable>,
          );
          return rows;
        })}
        {outbox.map((o) => (
          <View key={o.tempId} style={[styles.msgRow, styles.right, { marginTop: spacing.sm }]}>
            <Pressable onPress={() => o.status === 'failed' && deliver(o)} style={styles.bubbleWrap}>
              <View style={[styles.bubble, styles.bubbleMine, styles.tailMineFirst, styles.bubblePending]}>
                {o.media?.kind === 'image' ? <Image source={{ uri: o.media.dataUrl }} style={styles.image} resizeMode="cover" /> : null}
                {o.media?.kind === 'file' ? <Text style={[styles.msgText, styles.msgTextMine]}>📄 {o.media.name}</Text> : null}
                {o.text ? <Text style={[styles.msgText, styles.msgTextMine]}>{o.text}</Text> : null}
                <Text style={[styles.msgTime, styles.msgTimeMine]}>{o.status === 'failed' ? '⚠ не отправлено — нажми, чтобы повторить' : 'отправляется…'}</Text>
              </View>
            </Pressable>
          </View>
        ))}
        {!messages.length && !outbox.length && <Text style={styles.empty}>Сообщений пока нет. Напиши первым 👋</Text>}
      </ScrollView>

      {error ? <Text style={styles.err}>{error}</Text> : null}
      {pending ? (
        <View style={styles.stage}>
          {pending.kind === 'image'
            ? <Image source={{ uri: pending.dataUrl }} style={styles.stageImg} />
            : <Text style={styles.stageIcon}>📄</Text>}
          <Text style={styles.stageName} numberOfLines={1}>{pending.name || 'фото'}{pending.size ? ` · ${fmtSize(pending.size)}` : ''}</Text>
          <Pressable onPress={() => setPending(null)}><Text style={styles.stageX}>убрать ✕</Text></Pressable>
        </View>
      ) : null}

      <View style={styles.composer}>
        <Pressable onPress={() => setMenu((v) => !v)} hitSlop={8} style={styles.attach}><Text style={styles.attachIcon}>📎</Text></Pressable>
        <TextInput
          style={styles.input}
          value={text}
          onChangeText={setText}
          placeholder="Сообщение"
          placeholderTextColor={colors.textMuted}
          multiline
        />
        <Pressable onPress={send} style={styles.sendBtn}><Text style={styles.sendText}>➤</Text></Pressable>
      </View>

      {menu ? (
        <Pressable style={styles.menuBackdrop} onPress={() => setMenu(false)}>
          <View style={styles.menu}>
            <Pressable style={styles.menuItem} onPress={attachPhoto}><Text style={styles.menuText}>📷  Фото</Text></Pressable>
            <Pressable style={styles.menuItem} onPress={attachFile}><Text style={styles.menuText}>📄  Файл</Text></Pressable>
          </View>
        </Pressable>
      ) : null}

      <Modal visible={!!viewer} transparent animationType="fade" onRequestClose={() => setViewer(null)}>
        <Pressable style={styles.viewerWrap} onPress={() => setViewer(null)}>
          {viewer ? <Image source={{ uri: viewer }} style={styles.viewerImg} resizeMode="contain" /> : null}
          <Text style={styles.viewerHint}>нажми, чтобы закрыть</Text>
        </Pressable>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background },
  subhead: { ...typography.caption, color: colors.textMuted, textAlign: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.border },
  subheadOnline: { color: colors.success, fontWeight: '700' },
  list: { padding: spacing.md, paddingBottom: spacing.lg },
  daySep: { ...typography.caption, color: colors.textSecondary, textAlign: 'center', backgroundColor: colors.surfaceElevated, alignSelf: 'center', paddingHorizontal: spacing.md, paddingVertical: 3, borderRadius: radius.full, marginVertical: spacing.md, overflow: 'hidden' },
  system: { ...typography.caption, color: colors.textMuted, textAlign: 'center', marginVertical: spacing.sm, paddingHorizontal: spacing.lg },
  msgRow: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.xs, maxWidth: '90%' },
  bubbleWrap: { flexShrink: 1 },
  left: { alignSelf: 'flex-start' },
  right: { alignSelf: 'flex-end' },
  avatar: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: colors.onPrimary, fontWeight: '900', fontSize: 11 },
  avatarSpacer: { width: 28 },
  bubble: { borderRadius: radius.md, padding: spacing.sm + 2, gap: 4, minWidth: 66 },
  bubbleMine: { backgroundColor: colors.primary },
  bubbleOther: { backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border },
  tailMineFirst: { borderTopRightRadius: 6 },
  tailMine: { borderTopRightRadius: 6, borderBottomRightRadius: 6 },
  tailOtherFirst: { borderTopLeftRadius: 6 },
  tailOther: { borderTopLeftRadius: 6, borderBottomLeftRadius: 6 },
  bubblePending: { opacity: 0.6 },
  author: { ...typography.caption, fontWeight: '800', fontSize: 12, marginBottom: 2, marginLeft: 2 },
  msgText: { ...typography.body, color: colors.textPrimary, fontSize: 14 },
  msgTextMine: { color: colors.onPrimary },
  msgTime: { ...typography.caption, color: colors.textMuted, fontSize: 10, alignSelf: 'flex-end' },
  msgTimeMine: { color: 'rgba(247,241,227,0.7)' },
  image: { width: 220, height: 220, borderRadius: radius.sm, backgroundColor: colors.surfaceElevated },
  file: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 2, minWidth: 180 },
  fileIcon: { fontSize: 24 },
  fileName: { ...typography.body, color: colors.textPrimary, fontSize: 13.5, fontWeight: '700' },
  fileMeta: { ...typography.caption, color: colors.textMuted, fontSize: 10 },
  grow: { flex: 1 },
  viewerWrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.94)', alignItems: 'center', justifyContent: 'center', padding: spacing.md, gap: spacing.md },
  viewerImg: { width: '100%', height: '86%' },
  viewerHint: { ...typography.caption, color: 'rgba(255,255,255,0.6)' },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center', marginTop: spacing.xxl },
  err: { ...typography.caption, color: colors.danger, paddingHorizontal: spacing.md, paddingBottom: 4 },
  stage: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  stageImg: { width: 44, height: 44, borderRadius: radius.sm },
  stageIcon: { fontSize: 28 },
  stageName: { ...typography.caption, color: colors.textSecondary, flex: 1 },
  stageX: { ...typography.caption, color: colors.textSecondary },
  composer: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, padding: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.surface },
  attach: { paddingHorizontal: 6, paddingVertical: 8 },
  attachIcon: { fontSize: 20 },
  input: { flex: 1, maxHeight: 120, backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2, color: colors.textPrimary, fontFamily: typography.body.fontFamily, fontSize: 15 },
  sendBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
  sendText: { color: colors.onPrimary, fontSize: 16, fontWeight: '900' },
  menuBackdrop: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 },
  menu: { position: 'absolute', left: spacing.sm, bottom: 62, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, paddingVertical: 4, minWidth: 150 },
  menuItem: { paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.md },
  menuText: { ...typography.body, color: colors.textPrimary, fontSize: 14 },
});
